import { useEffect, useId, useState } from "react";
import styled from "styled-components";
import rovrPlaceholder from "@src/assets/img/placeholder.png";
import { buildMediaUrl } from "@src/utils/mediaUrl";

type CategoryIconProps = {
  url?: string | null;
  alt: string;
};

const CategoryIconImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: filter 0.2s;
  user-drag: none;
  -webkit-user-drag: none;
  -moz-user-select: none;
`;

/**
 * Holds the tint filter. Takes no layout space, and lives inside the button so
 * `currentColor` resolves to that button's colour.
 */
const TintDefs = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

/**
 * A playlist category icon, resilient to how the tag was uploaded in Strapi.
 *
 * Two things go wrong with the raw asset. Strapi returns a root-relative
 * `/uploads/...` path for locally-stored media and an absolute URL for S3-backed
 * media, so a tag uploaded under the local provider resolved against the dash
 * origin instead of the API and 404'd — `buildMediaUrl` puts the API origin back
 * on relative paths, and anything still unreachable falls back to the ROVR
 * placeholder. And each tag carries whatever colour it happened to be drawn in:
 * DANCE ships a near-black SVG while its siblings are yellow PNGs, which left
 * DANCE all but invisible against the dark modal [ROVR-3982].
 *
 * So the icon is recoloured rather than drawn as-is: the filter floods the
 * button's own colour through the icon's alpha, which makes every tag read the
 * same and lets the picked state flip to black without a second asset. A CSS
 * mask would be the tidier way to do this, but mask images are fetched with
 * CORS and the S3 bucket serves these assets without `Access-Control-Allow-Origin`.
 */
const CategoryIcon: React.FC<CategoryIconProps> = ({ url, alt }) => {
  const resolvedUrl = buildMediaUrl(url);
  const [src, setSrc] = useState(resolvedUrl || rovrPlaceholder);
  // Ids have to be unique per icon: several of these render side by side.
  const filterId = `category-icon-tint-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    setSrc(resolvedUrl || rovrPlaceholder);
  }, [resolvedUrl]);

  return (
    <>
      <TintDefs aria-hidden focusable="false">
        <filter id={filterId} colorInterpolationFilters="sRGB">
          <feFlood floodColor="currentColor" />
          <feComposite in2="SourceAlpha" operator="in" />
        </filter>
      </TintDefs>
      <CategoryIconImg
        src={src}
        alt={alt}
        className="category-icon"
        style={{ userSelect: "none", filter: `url(#${filterId})` }}
        onError={() => setSrc(rovrPlaceholder)}
      />
    </>
  );
};

export default CategoryIcon;
